import { useEffect, useState } from "react";
import { X, Play, Pause } from "lucide-react";

/**
 * Animated inline "trailer" that cycles through the studio phases inside a 9:16 phone.
 * Purely CSS/JS — no external video asset needed.
 */
const SCENES = [
    { id: "concept",   overline: "01 · CONCEPT",    title: "Chat · Humour · Storytime", accent: "#FF4D00", hook: "Gaston va au bureau." },
    { id: "character", overline: "02 · CHARACTER",  title: "GASTON",                    accent: "#FFB800", hook: "Gros chat roux, sarcastique." },
    { id: "script",    overline: "03 · SCRIPT",     title: "L'entretien d'embauche",    accent: "#0099FF", hook: "« Personne ne l'a fait avant lui. »" },
    { id: "storyboard",overline: "04 · STORYBOARD", title: "6 shots · 45s",              accent: "#10B981", hook: "Nano Banana render → PNG × 6" },
    { id: "publish",   overline: "05 · PUBLISH",    title: "GO LIVE",                    accent: "#FF4D00", hook: "12M vues · 247K abonnés" },
];

const SCENE_MS = 3200;

export default function TrailerModal({ open, onClose }) {
    const [i, setI] = useState(0);
    const [paused, setPaused] = useState(false);

    useEffect(() => {
        if (!open || paused) return;
        const t = setInterval(() => setI((n) => (n + 1) % SCENES.length), SCENE_MS);
        return () => clearInterval(t);
    }, [open, paused]);

    useEffect(() => { if (open) setI(0); }, [open]);

    if (!open) return null;
    const scene = SCENES[i];

    return (
        <div
            data-testid="trailer-modal"
            className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
        >
            <button
                data-testid="trailer-close"
                onClick={onClose}
                className="absolute top-6 right-6 w-10 h-10 rounded-full bg-[#161D2A] border border-[#253046] hover:border-[#FF4D00] grid place-items-center text-slate-300 hover:text-white kinetic"
            >
                <X className="w-5 h-5" />
            </button>

            <div className="relative" onClick={(e) => e.stopPropagation()}>
                <div className="absolute -inset-4 flame-gradient rounded-3xl blur-3xl opacity-25" />
                <div className="relative w-[320px] sm:w-[380px] aspect-[9/16] rounded-3xl border-2 border-[#253046] bg-gradient-to-b from-[#161D2A] via-[#0F141C] to-[#080B10] overflow-hidden shadow-2xl scanline">
                    <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-black/60 border border-red-500/40">
                        <span className="w-1.5 h-1.5 bg-red-500 rounded-full rec-dot" />
                        <span className="text-[10px] font-mono-sf text-red-400">REC · TRAILER</span>
                    </div>
                    <div className="absolute top-3 right-3 text-[10px] font-mono-sf px-2 py-0.5 rounded bg-black/60 border border-[#253046] text-slate-100">9:16</div>

                    {/* Progress bars */}
                    <div className="absolute top-12 left-4 right-4 flex gap-1">
                        {SCENES.map((s, idx) => (
                            <div key={s.id} className="flex-1 h-0.5 bg-white/15 rounded overflow-hidden">
                                <div
                                    className="h-full transition-all"
                                    style={{
                                        background: s.accent,
                                        width: idx < i ? "100%" : idx === i ? (paused ? "45%" : "100%") : "0%",
                                        transitionDuration: idx === i && !paused ? `${SCENE_MS}ms` : "180ms",
                                        transitionTimingFunction: "linear",
                                    }}
                                />
                            </div>
                        ))}
                    </div>

                    {/* Scene content with keyed re-render for animation */}
                    <div key={scene.id} className="absolute inset-0 flex flex-col justify-center px-8 animate-scene">
                        <div className="w-16 h-1 rounded" style={{ background: scene.accent }} />
                        <div className="font-mono-sf text-[10px] tracking-widest mt-3" style={{ color: scene.accent }}>{scene.overline}</div>
                        <div className="font-display uppercase text-white font-black text-4xl leading-none mt-2">{scene.title}</div>
                        <div className="mt-4 text-sm text-slate-200 italic">« {scene.hook} »</div>

                        {/* Decorative element per scene */}
                        <div className="mt-8 flex gap-1.5">
                            {scene.id === "storyboard" && Array.from({ length: 6 }).map((_, k) => (
                                <div key={k} className="w-8 h-12 rounded border" style={{ borderColor: scene.accent, background: `${scene.accent}22` }} />
                            ))}
                            {scene.id === "character" && (
                                <div className="flex gap-2">
                                    {["#FF7F50", "#FFB800", "#8B4513", "#F5DEB3", "#000000"].map((c, k) => (
                                        <div key={k} className="w-6 h-6 rounded-full border border-white/20" style={{ background: c }} />
                                    ))}
                                </div>
                            )}
                            {scene.id === "publish" && (
                                <div className="flex flex-col gap-1 mt-2 font-mono-sf text-xs text-slate-300">
                                    <div>#gaston · #chatIA · #shortsFR</div>
                                    <div style={{ color: scene.accent }}>▲ 12M views · 247K subs</div>
                                </div>
                            )}
                            {scene.id === "script" && (
                                <div className="mt-2 space-y-1 text-[11px] font-mono-sf text-slate-400 leading-relaxed">
                                    <div><span style={{ color: scene.accent }}>[0-3s]</span>  HOOK</div>
                                    <div><span style={{ color: scene.accent }}>[3-15s]</span> SETUP</div>
                                    <div><span style={{ color: scene.accent }}>[15-45s]</span> PAYOFF</div>
                                </div>
                            )}
                            {scene.id === "concept" && (
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                    {["cat", "office", "sarcasm", "series"].map((tg) => (
                                        <span key={tg} className="text-[10px] px-2 py-0.5 rounded border" style={{ borderColor: scene.accent, color: scene.accent }}>{tg}</span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="absolute bottom-4 left-4 right-4 flex items-center gap-2">
                        <button
                            data-testid="trailer-play-pause"
                            onClick={() => setPaused((p) => !p)}
                            className="w-9 h-9 rounded-full bg-white/95 grid place-items-center text-black hover:brightness-105 kinetic"
                        >
                            {paused ? <Play className="w-4 h-4 ml-0.5" fill="currentColor" /> : <Pause className="w-4 h-4" fill="currentColor" />}
                        </button>
                        <div className="flex-1 h-1 bg-white/15 rounded" />
                        <span className="font-mono-sf text-[10px] text-white/70">SHORTS FACTORY</span>
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes scene-in {
                    0%   { opacity: 0; transform: translateY(12px); }
                    100% { opacity: 1; transform: translateY(0); }
                }
                .animate-scene { animation: scene-in .6s ease-out both; }
            `}</style>
        </div>
    );
}
